import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Settings as SettingsIcon,
  UserPlus,
  Shield,
  Users as UsersIcon,
  User as UserIcon,
  ChevronLeft,
  CheckCircle2,
  Mail,
  Loader2,
} from "lucide-react";

/**
 * Admin Settings page.
 * - Reachable from the admin's name in the topbar/sidebar.
 * - Lets the admin manually add a new user with role: Admin / Manager / Sales.
 * - Each role automatically maps to the right backend permissions:
 *      Admin    → role=admin,        user_type=salesperson  (full system access)
 *      Manager  → role=salesperson,  user_type=manager      (lead a team, see "My team", receive AI summaries)
 *      Sales    → role=salesperson,  user_type=salesperson  (take courses, evals, redeem points)
 */

const ROLES = [
  {
    key: "admin",
    icon: Shield,
    color: "#1B2E7A",
    bg: "bg-[#1B2E7A]",
    softBg: "bg-[#1B2E7A]/10",
    border: "border-[#1B2E7A]",
    role: "admin",
    user_type: "salesperson",
  },
  {
    key: "manager",
    icon: UsersIcon,
    color: "#F4B81F",
    bg: "bg-[#F4B81F]",
    softBg: "bg-[#F4B81F]/15",
    border: "border-[#F4B81F]",
    role: "salesperson",
    user_type: "manager",
  },
  {
    key: "sales",
    icon: UserIcon,
    color: "#1B2E7A",
    bg: "bg-[#1B2E7A]",
    softBg: "bg-[#1B2E7A]/10",
    border: "border-[#1B2E7A]",
    role: "salesperson",
    user_type: "salesperson",
  },
];

function emailLooksValid(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
}

function genPassword(len = 10) {
  const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

export default function AdminSettings() {
  const t = useT();
  const { user } = useAuth();
  const [allUsers, setAllUsers] = useState([]);
  const [roleKey, setRoleKey] = useState("sales");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(() => genPassword());
  const [phone, setPhone] = useState("");
  const [managerId, setManagerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [sendingCreds, setSendingCreds] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null);

  const roleDef = useMemo(() => ROLES.find((r) => r.key === roleKey) || ROLES[2], [roleKey]);

  const managers = useMemo(
    () => allUsers.filter((u) => u.user_type === "manager" && u.role !== "admin"),
    [allUsers],
  );

  const loadUsers = async () => {
    try {
      const r = await api.get("/admin/users");
      setAllUsers(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      // ignore — admin-only endpoint, layout already gates access
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const reset = () => {
    setName("");
    setEmail("");
    setPassword(genPassword());
    setPhone("");
    setManagerId("");
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!name.trim()) {
      toast.error(t("settings.err.name"));
      return;
    }
    if (!emailLooksValid(email)) {
      toast.error(t("settings.err.email"));
      return;
    }
    if (password.length < 6) {
      toast.error(t("settings.err.password"));
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim(),
        password,
        role: roleDef.role,
        user_type: roleDef.user_type,
      };
      if (roleKey === "sales" && managerId) payload.manager_id = managerId;
      if (phone.trim()) payload.phone = phone.trim();
      const r = await api.post("/admin/users", payload);
      toast.success(t("settings.created_ok", { name: name.trim() }));
      setCreatedInfo({
        user_id: r.data?.user_id,
        name: r.data?.name || name.trim(),
        email: r.data?.email || email.trim(),
        role_label: t(`settings.role.${roleKey}.title`),
        role_key: roleKey,
        password,
        credentials_sent: false,
      });
      reset();
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("settings.err.create"));
    } finally {
      setBusy(false);
    }
  };

  const sendCredentials = async () => {
    if (!createdInfo?.user_id) return;
    setSendingCreds(true);
    try {
      const r = await api.post(`/admin/users/${createdInfo.user_id}/send-credentials`, {
        password: createdInfo.password,
        role_label: createdInfo.role_label,
      });
      if (r.data?.status === "sent") {
        toast.success(t("settings.creds_sent", { email: createdInfo.email }));
        setCreatedInfo((prev) => prev ? { ...prev, credentials_sent: true } : prev);
      } else if (r.data?.status === "mocked") {
        toast.success(t("settings.creds_mocked"));
        setCreatedInfo((prev) => prev ? { ...prev, credentials_sent: true } : prev);
      } else if (r.data?.status === "error") {
        toast.error(r.data?.error || t("settings.creds_error"));
      } else {
        toast.error(t("settings.creds_error"));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("settings.creds_error"));
    } finally {
      setSendingCreds(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-settings-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/admin"
            className="inline-flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#1B2E7A] mb-2"
            data-testid="settings-back-link"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> {t("settings.back")}
          </Link>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280] font-medium">
            {t("settings.eyebrow")}
          </p>
          <h1 className="font-heading text-3xl md:text-4xl text-[#1A1A1A] mt-1 inline-flex items-center gap-3">
            <SettingsIcon className="h-7 w-7 text-[#1B2E7A]" strokeWidth={1.6} />
            {t("settings.title")}
          </h1>
          <p className="text-sm text-[#374151] max-w-2xl mt-2">
            {t("settings.subtitle", { name: user?.name || "" })}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">{t("settings.you")}</p>
          <p className="font-heading text-base text-[#1A1A1A]">{user?.name}</p>
          <p className="text-xs text-[#6B7280]">{user?.email}</p>
        </div>
      </header>

      <section className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(0,420px)] gap-6">
        {/* Add user form */}
        <form
          onSubmit={submit}
          className="bg-white border border-[#E5E7EB] rounded-xl p-6 md:p-8"
          data-testid="add-user-form"
        >
          <div className="flex items-center gap-2 mb-1">
            <UserPlus className="h-5 w-5 text-[#1B2E7A]" strokeWidth={1.6} />
            <h2 className="font-heading text-xl text-[#1A1A1A]">{t("settings.add.title")}</h2>
          </div>
          <p className="text-sm text-[#6B7280] mb-6">{t("settings.add.subtitle")}</p>

          {/* Step 1 — Role pills */}
          <div className="mb-6">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#6B7280] font-medium mb-2">
              {t("settings.step", { n: 1 })} · {t("settings.field.role")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {ROLES.map((r) => {
                const Icon = r.icon;
                const active = roleKey === r.key;
                return (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => setRoleKey(r.key)}
                    data-testid={`role-pick-${r.key}`}
                    className={`text-left p-4 rounded-lg border transition-all ${
                      active
                        ? `${r.border} ring-2 ring-offset-2 ring-[${r.color}]/40 bg-white`
                        : "border-[#E5E7EB] bg-white hover:border-[#6B7280]"
                    }`}
                    style={active ? { boxShadow: `inset 0 0 0 1px ${r.color}` } : {}}
                  >
                    <div className={`h-8 w-8 rounded-md ${r.softBg} flex items-center justify-center mb-2`}>
                      <Icon className="h-4 w-4" strokeWidth={1.8} style={{ color: r.color }} />
                    </div>
                    <p className="font-heading text-sm text-[#1A1A1A]">
                      {t(`settings.role.${r.key}.title`)}
                    </p>
                    <p className="text-[11px] text-[#6B7280] mt-0.5 leading-snug">
                      {t(`settings.role.${r.key}.tagline`)}
                    </p>
                  </button>
                );
              })}
            </div>
            {/* Permissions panel */}
            <div className={`mt-3 p-3 rounded-md border ${roleDef.border} ${roleDef.softBg}`} data-testid="role-permissions">
              <p className="text-[10px] uppercase tracking-[0.16em] font-medium" style={{ color: roleDef.color }}>
                {t("settings.permissions_title")}
              </p>
              <ul className="mt-1.5 space-y-1">
                {[1, 2, 3, 4].map((i) => {
                  const k = `settings.role.${roleKey}.perm_${i}`;
                  const txt = t(k);
                  if (!txt || txt === k) return null;
                  return (
                    <li key={i} className="flex items-start gap-1.5 text-xs text-[#1A1A1A]">
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: roleDef.color }} />
                      <span>{txt}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Step 2 — Identity */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#6B7280] font-medium mb-2">
                {t("settings.step", { n: 2 })} · {t("settings.identity")}
              </p>
            </div>
            <div>
              <label className="block text-xs text-[#374151] mb-1" htmlFor="add-user-name">
                {t("settings.field.name")}
              </label>
              <Input
                id="add-user-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="María González"
                data-testid="add-user-name"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-xs text-[#374151] mb-1" htmlFor="add-user-email">
                {t("settings.field.email")}
              </label>
              <Input
                id="add-user-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.toLowerCase())}
                placeholder="maria@canalesfranchise.com"
                data-testid="add-user-email"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-xs text-[#374151] mb-1" htmlFor="add-user-password">
                {t("settings.field.password")}
              </label>
              <div className="flex gap-2">
                <Input
                  id="add-user-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="font-mono text-sm"
                  data-testid="add-user-password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setPassword(genPassword())}
                  className="text-xs text-[#1B2E7A] hover:text-[#13205A] underline whitespace-nowrap"
                  data-testid="add-user-password-regen"
                >
                  {t("settings.regen")}
                </button>
              </div>
              <p className="text-[10px] text-[#6B7280] mt-1">{t("settings.field.password_hint")}</p>
            </div>
            <div>
              <label className="block text-xs text-[#374151] mb-1" htmlFor="add-user-phone">
                {t("settings.field.phone")}
              </label>
              <Input
                id="add-user-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+18175551234"
                className="font-mono"
                data-testid="add-user-phone"
                autoComplete="off"
              />
              <p className="text-[10px] text-[#6B7280] mt-1">{t("settings.field.phone_hint")}</p>
            </div>
          </div>

          {/* Step 3 — Manager assignment (only for sales) */}
          {roleKey === "sales" && (
            <div className="mb-6">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#6B7280] font-medium mb-2">
                {t("settings.step", { n: 3 })} · {t("settings.field.reports_to")}
              </p>
              <select
                value={managerId}
                onChange={(e) => setManagerId(e.target.value)}
                className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white"
                data-testid="add-user-manager"
              >
                <option value="">{t("settings.no_manager")}</option>
                {managers.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.name} · {m.email}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-[#6B7280] mt-1">{t("settings.field.reports_to_hint")}</p>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-3 pt-2 border-t border-[#E5E7EB]">
            <Button
              type="button"
              variant="outline"
              onClick={reset}
              data-testid="add-user-reset"
            >
              {t("settings.clear")}
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="bg-[#1B2E7A] hover:bg-[#13205A] text-white"
              data-testid="add-user-submit"
            >
              <UserPlus className="h-4 w-4 mr-1" />
              {busy ? t("settings.creating") : t("settings.add_btn", { role: t(`settings.role.${roleKey}.title`) })}
            </Button>
          </div>
        </form>

        {/* Sidebar: created confirmation + roles legend */}
        <aside className="space-y-4">
          {createdInfo ? (
            <div
              className="bg-[#1B2E7A] text-white rounded-xl p-5"
              data-testid="settings-created-card"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/70 font-medium">
                {t("settings.created_eyebrow")}
              </p>
              <p className="font-heading text-lg mt-1">{createdInfo.name}</p>
              <p className="text-xs text-white/80 break-all">{createdInfo.email}</p>
              <div className="mt-3 pt-3 border-t border-white/20">
                <p className="text-[10px] uppercase tracking-[0.18em] text-white/70 font-medium mb-1">
                  {t("settings.created_role")}
                </p>
                <p className="text-sm font-medium">{createdInfo.role_label}</p>
              </div>
              <div className="mt-3 pt-3 border-t border-white/20">
                <p className="text-[10px] uppercase tracking-[0.18em] text-white/70 font-medium mb-1">
                  {t("settings.created_password")}
                </p>
                <p className="font-mono text-sm bg-white/10 rounded px-2 py-1.5 break-all" data-testid="created-password">
                  {createdInfo.password}
                </p>
                <p className="text-[10px] text-white/70 mt-1.5">{t("settings.created_password_hint")}</p>
              </div>
              {/* Send credentials by email */}
              <div className="mt-3 pt-3 border-t border-white/20">
                {createdInfo.credentials_sent ? (
                  <div
                    className="inline-flex items-center gap-1.5 text-xs text-white/90 bg-white/10 rounded-md px-2.5 py-1.5"
                    data-testid="creds-sent-badge"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {t("settings.creds_sent_badge")}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={sendCredentials}
                    disabled={sendingCreds}
                    className="w-full inline-flex items-center justify-center gap-2 bg-white text-[#1B2E7A] hover:bg-[#FFFFFF] px-3 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-60"
                    data-testid="send-credentials-btn"
                  >
                    {sendingCreds ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t("settings.creds_sending")}
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4" />
                        {t("settings.creds_send_btn")}
                      </>
                    )}
                  </button>
                )}
                <p className="text-[10px] text-white/70 mt-1.5">{t("settings.creds_send_hint")}</p>
              </div>
              <button
                type="button"
                onClick={() => setCreatedInfo(null)}
                className="mt-4 text-xs text-white/80 hover:text-white underline"
                data-testid="settings-dismiss-created"
              >
                {t("settings.dismiss")}
              </button>
            </div>
          ) : null}

          <div className="bg-[#F3F4F6] border border-[#E5E7EB] rounded-xl p-5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280] font-medium">
              {t("settings.legend_eyebrow")}
            </p>
            <p className="font-heading text-base text-[#1A1A1A] mt-1">{t("settings.legend_title")}</p>
            <ul className="mt-3 space-y-3">
              {ROLES.map((r) => {
                const Icon = r.icon;
                return (
                  <li key={r.key} className="flex items-start gap-2.5">
                    <div
                      className={`h-7 w-7 shrink-0 rounded-md ${r.softBg} flex items-center justify-center`}
                    >
                      <Icon className="h-3.5 w-3.5" strokeWidth={1.8} style={{ color: r.color }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#1A1A1A]">
                        {t(`settings.role.${r.key}.title`)}
                      </p>
                      <p className="text-[11px] text-[#6B7280] leading-snug">
                        {t(`settings.role.${r.key}.legend`)}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="text-xs text-[#6B7280] px-1">
            {t("settings.full_table_hint")}{" "}
            <Link to="/admin" className="text-[#1B2E7A] hover:underline">
              {t("settings.go_to_dashboard")}
            </Link>
          </div>
        </aside>
      </section>
    </div>
  );
}
